'use client';

import type { ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { Toaster } from 'sonner';

import Overlay from '@/components/overlay';
import { cn } from '@/utils/cn';

import CookiePolicy from './cookie-policy';
import DeviceClockWarning from './DeviceClockWarning';
import Header from './header';
import PriceFeedNotice from './PriceFeedNotice';
import Sidebar from './sidebar';
import { getIsQuantaRoutes } from './utils';

interface MainProps {
  isCookieConsent: true | undefined;
  isPosthogIdentify: true | false | undefined;
  children: ReactNode;
}

const Main = ({ isCookieConsent, isPosthogIdentify, children }: MainProps) => {
  const pathname = usePathname();

  const isPrivacyPolicyPage = pathname === '/privacy-policy';
  const isVaultTransactionsPage = pathname === '/transactions';
  const isOverviewAllVaultsPage = pathname === '/overview/all-vaults';
  const isOverviewTransactionsPage = pathname === '/overview/transactions';
  const isReserveTransactionsPage = pathname === '/overview/reserve/transactions';

  const shouldRenderSidebar =
    !isVaultTransactionsPage && !isOverviewAllVaultsPage && !isOverviewTransactionsPage && !isReserveTransactionsPage;

  const shouldRenderOverlay = isPrivacyPolicyPage && !isCookieConsent;

  const shouldRenderHeader = !isPrivacyPolicyPage && shouldRenderSidebar;

  return (
    <div className="flex min-h-screen h-full">
      <Toaster position="top-right" duration={5000} visibleToasts={5} />
      {!isPrivacyPolicyPage && <CookiePolicy isCookieConsent={isCookieConsent} isPosthogIdentify={isPosthogIdentify} />}
      <div>
        {shouldRenderSidebar && <Sidebar />}
        {shouldRenderOverlay && <Overlay className="hidden lg:block fixed top-0 left-0 w-[264px]" />}
      </div>
      <div className={cn('relative flex flex-col h-full w-full pt-[74px]', shouldRenderSidebar && 'lg:ml-[264px] lg:pt-[94px]')}>
        {shouldRenderHeader && <Header />}
        <main className="flex flex-col h-full overflow-y-auto">
          {!isPrivacyPolicyPage && <DeviceClockWarning />}
          {!isPrivacyPolicyPage && !getIsQuantaRoutes(pathname) && <PriceFeedNotice />}
          {children}
        </main>
      </div>
    </div>
  );
};

export default Main;
